import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { X } from 'lucide-react';
import './TikTokLogin.css';

// TikTok SVG Icon
const TikTokIcon = () => (
  <svg width="24" height="24" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="24" fill="#fff"/>
    <path d="M32.5 14.5c-1.1 0-2-.9-2-2V10h-4v18.5c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4c.3 0 .7 0 1 .1v-4.1c-.3 0-.7-.1-1-.1-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8V22.7c1.2.5 2.6.8 4 .8v-4c-1.1 0-2-.9-2-2v-3z" fill="#010101"/>
    <path d="M32.5 14.5c-1.1 0-2-.9-2-2V10h-4v18.5c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4c.3 0 .7 0 1 .1v-4.1c-.3 0-.7-.1-1-.1-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8V22.7c1.2.5 2.6.8 4 .8v-4c-1.1 0-2-.9-2-2v-3z" fill="#25F4EE"/>
    <path d="M32.5 14.5c-1.1 0-2-.9-2-2V10h-4v18.5c0 2.2-1.8 4-4 4s-4-1.8-4-4 1.8-4 4-4c.3 0 .7 0 1 .1v-4.1c-.3 0-.7-.1-1-.1-4.4 0-8 3.6-8 8s3.6 8 8 8 8-3.6 8-8V22.7c1.2.5 2.6.8 4 .8v-4c-1.1 0-2-.9-2-2v-3z" fill="#FE2C55" fillOpacity=".7"/>
  </svg>
);

const TikTokShopOAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { login, setIsAuthenticated } = useAuth();

  const [showSubModal, setShowSubModal] = useState(false);
  const [subForm, setSubForm] = useState({ shopName: '', email: '', password: '' });

  const startOAuthFlow = async () => {
    try {
      setLoading(true);
      setError('');
      
      const urlParams = new URLSearchParams(window.location.search);
      const userIdFromUrl = urlParams.get('user_id');
      
      let requestUrl = '/tiktok/auth_url';
      if (userIdFromUrl) {
        requestUrl += `?user_id=${encodeURIComponent(userIdFromUrl)}`;
      }
      
      const response = await apiClient.get(requestUrl, {withCredentials: true, credentials: 'include'});
      const { authorization_url, user_id } = response.data;
      
      if (!user_id) {
        throw new Error("User ID not found. Please log in first.");
      }
      
      window.location.href = authorization_url;
      
    } catch (err) {
      setError(`Error: ${err.message}`);
      setLoading(false);
    }
  };

  const handleSubAccountSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('user_id');
        
        if (!userId) {
             throw new Error("User ID not found. Please login again.");
        }

        const payload = {
            shopName: subForm.shopName,
            userId: userId,
            customManager: subForm.email,
            customPass: subForm.password,
            customLinked: true,
            region: "us"
        };

        const response = await apiClient.post('/api/shops/create', payload);
        
        if (response.status === 201) {
            setSuccess("Shop connected! Redirecting...");
            setIsAuthenticated(true);
            localStorage.setItem("userId", userId);
             setTimeout(() => {
                navigate('/dashboard');
              }, 1500);
        }

    } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to connect");
    } finally {
        setLoading(false);
    }
  };

  const exchangeCode = useCallback(async (authCode) => {
    if (!authCode) return;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await apiClient.post('/tiktok/exchange_code?auth_code=' + encodeURIComponent(authCode));
      const newTokenData = response.data.token_data;
      login(newTokenData);
      setSuccess(`✅ Successful! Welcome ${newTokenData.seller_name}.`);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [login, navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      window.history.replaceState({}, document.title, window.location.pathname);
      exchangeCode(code);
    }
  }, [exchangeCode]);

  return (
    <div className="tiktok-login-container">
      {/* Left side (Marketing) */}
      <div className="tiktok-login-left">
        <div className="brand-logo-section">
          <div className="tiktok-brand-logo">
            shop<span>OS</span>.
          </div>
          <h1 className="tiktok-tagline">
            Connect your shop and scale your brand effortlessly
          </h1>
        </div>
      </div>

      {/* Right side (Auth) */}
      <div className="tiktok-login-right">
        <div className="tiktok-auth-box">
          <div className="tiktok-auth-header">
            <h1>Connect Shop</h1>
            <p>Select your authorization method to continue</p>
          </div>

          {(error || success) && (
            <div className={`status-box ${error ? 'error-status' : 'success-status'}`}>
              {error ? error : success}
            </div>
          )}
          
          <div className="auth-button-group">
            <button 
              className="main-shop-btn"
              onClick={startOAuthFlow} 
              disabled={loading}
            >
              <TikTokIcon />
              {loading && !showSubModal ? 'Connecting...' : 'Connect Main Shop'}
            </button>

            <button 
              className="sub-account-btn"
              onClick={() => setShowSubModal(true)} 
              disabled={loading}
            >
              Connect Sub Account
            </button>
          </div>

          <div className="auth-footer">
            <p>Secure authentication for verified brands</p>
          </div>
        </div>
      </div>

      {/* Sub Account Modal */}
      {showSubModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <button className="modal-close" onClick={() => setShowSubModal(false)}>
              <X size={24} />
            </button>
            
            <h2 className="modal-title">Sub Account Details</h2>
            <p style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: '500', marginTop: '-12px', marginBottom: '24px' }}>
              ⚠️ Only Connect the Activated shop
            </p>
            
            <form onSubmit={handleSubAccountSubmit}>
              <div className="sub-form-group">
                <label>Shop Name</label>
                <input 
                  required
                  type="text" 
                  className="checkout-input"
                  placeholder="Official Shop Name"
                  value={subForm.shopName}
                  onChange={e => setSubForm({...subForm, shopName: e.target.value})}
                />
              </div>
              <div className="sub-form-group">
                <label>Manager Email</label>
                <input 
                  required
                  type="email" 
                  className="checkout-input"
                  placeholder="manager@brand.com"
                  value={subForm.email}
                  onChange={e => setSubForm({...subForm, email: e.target.value})}
                />
              </div>
              <div className="sub-form-group">
                <label>Password</label>
                <input 
                  required
                  type="password" 
                  className="checkout-input"
                  placeholder="Account password"
                  value={subForm.password}
                  onChange={e => setSubForm({...subForm, password: e.target.value})}
                />
              </div>
              
              <button 
                type="submit"
                className="submit-sub-btn"
                disabled={loading}
              >
                {loading ? 'Connecting...' : 'Link Sub Account'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TikTokShopOAuth;